/**
 * Swept AABB physics against the cell grid.
 *
 * Three rules here carry almost all of the "does this feel like a platformer or
 * like a physics toy" weight:
 *
 *  1. **X and Y resolve independently.** Moving both at once and resolving the
 *     combined penetration is what causes wall-sticking and corner snagging.
 *     Two separate passes is the standard fix and it is not optional.
 *
 *  2. **Step-up.** In a destructible world every floor eventually becomes
 *     rubble. Without an automatic step over ledges of a few cells, the player
 *     catches on single-pixel debris constantly and the game feels broken. This
 *     one rule is worth more than any amount of acceleration tuning.
 *
 *  3. **Corner correction.** A jump whose head clips the corner of a ledge is
 *     universally read as the game's fault. We nudge horizontally and let the
 *     jump continue.
 *
 *  4. **One-way floors.** A semi-solid cell (`M.oneWay`) blocks a body only
 *     while it is descending onto the cell's top edge. Everything else —
 *     rising, walking, already being inside it, holding the drop-through
 *     ticket — passes through. This is the one primitive our seven material
 *     categories could not express (docs/10 §2.3).
 *
 * Submersion is a continuous fraction, never a boolean — a knee-deep puddle
 * must not feel like the ocean.
 */

import { Flag, M, OneWay, VOID } from '../sim/materials';
import type { CellWorld } from '../sim/world';
import { Category } from '../sim/materials';
import { BodyFlag, type World } from './ecs';

/** Max ledge height auto-stepped, in cells. */
export const STEP_UP_MAX = 3;

/** Max horizontal nudge when the head clips a ceiling corner, in cells. */
export const CORNER_CORRECTION = 4;

/**
 * Per-body overrides for the two forgiveness nudges.
 *
 * They live in `MovementTuning` for the player (`ledgeSnap`, `cornerCorrection`)
 * so docs/02's claim that every feel number is live-editable from the debug
 * panel is actually true. Bodies without a mover — thrown corpses, debris —
 * keep the module defaults.
 */
export interface StepLimits {
  /** Vertical nudge when the feet just miss a ledge. docs/02 §2.3 `ledgeSnap`. */
  stepUp: number;
  /** Horizontal nudge when the head clips a ceiling corner. */
  cornerCorrection: number;
  /**
   * Issue #179. Narrowest gap, in cells, this body can squeeze through —
   * see `squeezeBlocked` for how it is used. Default 10 matches the
   * harness/`defaultTuning()` body width, so bodies stepped with no mover
   * (thrown corpses, debris) get no squeeze allowance, same as before this
   * field existed.
   */
  minGapCells: number;
}

const DEFAULT_LIMITS: StepLimits = {
  stepUp: STEP_UP_MAX,
  cornerCorrection: CORNER_CORRECTION,
  minGapCells: 10,
};

/** Sub-steps are capped at this length so nothing tunnels. */
const MAX_SUBSTEP = 2;

/**
 * Slack used when rounding an AABB edge to a cell row, matched to the `-0.001`
 * in `solidIn` so a body resting exactly on a surface reads as supported
 * instead of oscillating between "on it" and "one ten-thousandth inside it".
 */
const EDGE_EPS = 0.001;

/**
 * Sentinel for `solidIn`'s `feetY`: "this probe has no descending feet", so
 * one-way cells are pure air. Every probe that is not *am I falling onto this*
 * wants exactly that — horizontal movement, step-up, corner correction and the
 * wall check must all pass straight through a platform. Positive infinity is
 * not a magic number here, it falls out of the predicate: feet infinitely far
 * below a surface can never land on it.
 */
export const ONE_WAY_IGNORE = Number.POSITIVE_INFINITY;

export interface BodyResult {
  hitX: boolean;
  hitY: boolean;
  landed: boolean;
  landingSpeed: number;
  steppedUp: boolean;
  cornerCorrected: boolean;
}

const result: BodyResult = {
  hitX: false,
  hitY: false,
  landed: false,
  landingSpeed: 0,
  steppedUp: false,
  cornerCorrected: false,
};

/**
 * True if any cell in the AABB footprint blocks movement.
 *
 * `feetY` is the *pre-move* world y of the body's feet, and it is the whole of
 * the one-way floor rule: a semi-solid cell blocks only a body whose feet were
 * at or above that cell's top edge before the move, which is exactly "I am
 * landing on it" and excludes "I am rising through it", "I am walking through
 * it" and "I am already inside it". Omit it (the default) and semi-solids are
 * not solid at all.
 *
 * Passing the pre-move feet rather than the post-move ones is not a detail:
 * with the post-move value a fast fall has already crossed the surface by the
 * time it is tested, the predicate reads false, and the body tunnels through
 * the platform. That is the classic bug and this argument is where it is
 * prevented.
 */
export function solidIn(
  cells: CellWorld,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  phased: boolean,
  feetY: number = ONE_WAY_IGNORE,
  /**
   * Issue #174, split from #159. Breaker/Spectral entity's `passesLooseSolids`
   * — a loose cell (`Category.LOOSE`: sand, ash, gravel, snow) does not block
   * a body carrying this, the same way `phased` already ignores rock. Default
   * `false` so every call site written before this parameter existed is
   * unchanged.
   */
  passLoose: boolean = false,
): boolean {
  if (phased) return false;
  const x0 = Math.floor(cx - halfW);
  const x1 = Math.floor(cx + halfW - 0.001);
  const y0 = Math.floor(cy - halfH);
  const y1 = Math.floor(cy + halfH - 0.001);
  // Skipping the whole one-way branch when nothing can land keeps the extra
  // `peek` out of the common probes, which run several times per substep.
  const landing = feetY !== ONE_WAY_IGNORE;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (cells.isSolid(x, y)) {
        if (passLoose && M.category[cells.peek(x, y)] === Category.LOOSE) continue;
        return true;
      }
      if (!landing || feetY > y + EDGE_EPS) continue;
      // `y` is the cell's top edge in world units.
      const m = cells.peek(x, y);
      if (m === VOID || M.oneWay[m] !== OneWay.UP) continue;
      if ((cells.getFlags(x, y) & Flag.PHASED) !== 0) continue;
      return true;
    }
  }
  return false;
}

/**
 * Issue #179. Whether a body's *vertical* movement into `(cx, cy)` is
 * blocked, once a narrower-than-the-body squeeze allowance is accounted for.
 *
 * `solidIn` alone answers "does the body's real box overlap solid" — the
 * right question for landing, wall checks and step-up, where the body's
 * actual width matters. It is the wrong question for whether a body whose
 * archetype grants `minGapCells` narrower than its own width can descend a
 * vertical shaft the real box does not fit: docs/02 §3.2's "squeezes through
 * 3-cell gaps" (Semi-liquid mutant, body width 12) and "passes through
 * 1-cell gaps" (Modular drone swarm, body width 10) both author
 * `minGapCells` *below* their own body width on purpose. A plain "refuse if
 * the gap is narrower than `minGapCells`" check is a no-op for every shipped
 * archetype, since `solidIn`'s own box test already refuses anything
 * narrower than the body's width, which is always >= `minGapCells` — so
 * passing the shaft at all requires this function to widen what counts as
 * passable, not add a second refusal on top of one already stricter.
 *
 * Scoped to descent (the Y sweep) rather than horizontal movement (the X
 * sweep) on purpose: a channel a body squeezes *down* through is a static
 * width check at whatever `cx` the body already occupies — the walls flank
 * the path rather than sitting in it, so nothing has to cross solid rock to
 * reach the check. A horizontal equivalent (walking through a gap punched in
 * an otherwise-continuous wall) does not have that property: the wall
 * itself sits directly in the travel path, and `MAX_SUBSTEP`'s
 * tunnelling guard means no increment can ever land the body's centre past
 * the wall's own thickness to reach the gap — a real obstacle for a future
 * pass, not one this function papers over.
 *
 * When the real box is not blocked, this is `solidIn`, unchanged. When the
 * real box is blocked and `minGapCells` is narrower than the body's width,
 * the body is retested at half-width shrunk to `minGapCells / 2` — a probe
 * no wider than the archetype's own claimed squeeze — and only stays
 * blocked if even that narrower probe collides. `halfH` is untouched: the
 * squeeze narrows the dimension the flanking walls actually constrain, not
 * the body's height.
 */
function squeezeBlocked(
  cells: CellWorld,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  phased: boolean,
  passLoose: boolean,
  minGapCells: number,
  feetY: number = ONE_WAY_IGNORE,
): boolean {
  if (!solidIn(cells, cx, cy, halfW, halfH, phased, feetY, passLoose)) return false;
  const fullWidth = halfW * 2;
  if (minGapCells >= fullWidth) return true;
  const eff = Math.max(0.5, minGapCells / 2);
  return solidIn(cells, cx, cy, eff, halfH, phased, feetY, passLoose);
}

/**
 * Dominant material under the feet, plus how much of the footprint it covers.
 * Friction and wall-slide speed read this, which is what makes ice, fungus and
 * oil feel different to stand on and to cling to.
 */
export function groundMaterialAt(
  cells: CellWorld,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
): number {
  const y = Math.floor(cy + halfH + 0.5);
  const x0 = Math.floor(cx - halfW);
  const x1 = Math.floor(cx + halfW - 0.001);
  let best = VOID;
  let bestCount = 0;
  let count = 0;
  let current = VOID;
  for (let x = x0; x <= x1; x++) {
    const m = cells.peek(x, y);
    if (m === current) {
      count++;
    } else {
      current = m;
      count = 1;
    }
    if (m !== VOID && count > bestCount) {
      bestCount = count;
      best = m;
    }
  }
  return best;
}

/** How far each `groundSlopeAt` sample sits from centre, past the body's own half-width, in cells. */
const SLOPE_SAMPLE_OFFSET = 4;

/**
 * Signed downhill grade under the feet — issue #180. 0 on flat ground or
 * when no surface is found nearby (a ledge, a cliff edge); otherwise
 * roughly rise-over-run, positive when the ground descends to the right
 * (+x is downhill), negative when it descends to the left, clamped in
 * practice to [-1, 1] by callers rather than here (a vertical cliff sampled
 * at the edge would otherwise read as a much steeper "grade" than any real
 * slope, and callers want a bias, not a literal angle).
 *
 * Two point-samples rather than a full profile: `slopeBias`'s whole job is
 * "does the ground under me trend downhill or up," not the exact curve of
 * it, and a coarse read is what a per-substep call can afford.
 */
export function groundSlopeAt(
  cells: CellWorld,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
): number {
  const feetY = cy + halfH;
  const d = SLOPE_SAMPLE_OFFSET + halfW;
  // The search range has to cover the steepest slope this function still
  // wants to read (a 45° ramp, grade 1) at this offset — a range shorter
  // than `d` misses the surface entirely on any ramp that steep, silently
  // reading it as "no surface" instead.
  const leftY = surfaceHeightNear(cells, cx - d, feetY, d);
  const rightY = surfaceHeightNear(cells, cx + d, feetY, d);
  if (leftY === null || rightY === null) return 0;
  return (rightY - leftY) / (2 * d);
}

/** World y of the first solid-over-air transition near `aroundY`, within `range` cells, or null if none is. */
function surfaceHeightNear(cells: CellWorld, x: number, aroundY: number, range: number): number | null {
  const xi = Math.floor(x);
  const y0 = Math.floor(aroundY);
  for (let dy = -range; dy <= range; dy++) {
    const y = y0 + dy;
    if (cells.isSolid(xi, y) && !cells.isSolid(xi, y - 1)) return y;
  }
  return null;
}

/**
 * Fraction of the body's height immersed in liquid, and the dominant liquid.
 * Returns submersion in [0,1]; writes the material into `w.submergedMat`.
 */
export function sampleSubmersion(
  w: World,
  i: number,
  cells: CellWorld,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
): number {
  const x0 = Math.floor(cx - halfW);
  const x1 = Math.floor(cx + halfW - 0.001);
  const y0 = Math.floor(cy - halfH);
  const y1 = Math.floor(cy + halfH - 0.001);
  const total = (x1 - x0 + 1) * (y1 - y0 + 1);
  if (total <= 0) return 0;

  let wet = 0;
  let dominant = VOID;
  let dominantCount = 0;
  // Small tally rather than a Map — bodies span a handful of materials at most
  // and a Map allocation per body per frame is not affordable.
  let m0 = VOID;
  let c0 = 0;
  let m1 = VOID;
  let c1 = 0;

  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const m = cells.peek(x, y);
      if (m === VOID) continue;
      const cat = M.category[m];
      if (cat !== Category.LIQUID && cat !== Category.SLUDGE) continue;
      wet++;
      if (m === m0) c0++;
      else if (m === m1) c1++;
      else if (c0 === 0) {
        m0 = m;
        c0 = 1;
      } else if (c1 === 0) {
        m1 = m;
        c1 = 1;
      }
    }
  }
  if (c0 >= c1) {
    dominant = m0;
    dominantCount = c0;
  } else {
    dominant = m1;
    dominantCount = c1;
  }
  void dominantCount;

  w.submergedMat[i] = dominant;
  return wet / total;
}

/**
 * Integrate one body against the grid. `dt` is the substep duration.
 * Returns a shared result object — read it before the next call.
 */
export function stepBody(
  w: World,
  i: number,
  cells: CellWorld,
  dt: number,
  limits: StepLimits = DEFAULT_LIMITS,
): BodyResult {
  result.hitX = false;
  result.hitY = false;
  result.landed = false;
  result.landingSpeed = 0;
  result.steppedUp = false;
  result.cornerCorrected = false;

  const flags = w.bodyFlags[i];
  const phased = (flags & BodyFlag.PHASED) !== 0 || (flags & BodyFlag.IGNORE_TERRAIN) !== 0;
  // The movement layer raises this for a short window; here it simply means
  // "one-way floors are air for this body right now".
  const dropThrough = (flags & BodyFlag.DROP_THROUGH) !== 0;
  // Issue #174: `stepMover` mirrors the mover's own `passesLooseSolids` tuning
  // into this flag every substep, the same relay it already runs for `PHASED`.
  const passLoose = (flags & BodyFlag.BURROWS) !== 0;
  const halfW = w.halfW[i];
  const halfH = w.halfH[i];

  let x = w.x[i];
  let y = w.y[i];
  let vx = w.vx[i];
  let vy = w.vy[i];

  // --- environment sampling ---
  const sub = sampleSubmersion(w, i, cells, x, y, halfW, halfH);
  w.submersion[i] = sub;

  if (sub > 0.02) {
    const lm = w.submergedMat[i];
    if (lm !== VOID) {
      // Drag only. Buoyancy is a *force*, so it belongs with gravity in the
      // controller (`applyVertical`), and it used to be applied here as well —
      // two independently tuned buoyancy models multiplied together, which is
      // why water never behaved the way either of them said it would.
      // docs/02 §2.7 specifies exactly one.
      const drag = 1 + M.viscosity[lm] * 8;
      const k = Math.max(0, 1 - drag * sub * dt * 2.2);
      vx *= k;
      vy *= k;
    }
  }

  // --- X axis ---
  const dx = vx * dt;
  if (dx !== 0 && !phased) {
    const steps = Math.max(1, Math.ceil(Math.abs(dx) / MAX_SUBSTEP));
    const inc = dx / steps;
    for (let s = 0; s < steps; s++) {
      const nx = x + inc;
      if (!solidIn(cells, nx, y, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose)) {
        x = nx;
        continue;
      }

      // Blocked. Try stepping up — this is what stops rubble from being a wall.
      let stepped = false;
      if ((flags & BodyFlag.NO_STEP_UP) === 0) {
        for (let up = 1; up <= limits.stepUp; up++) {
          if (
            !solidIn(cells, nx, y - up, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose) &&
            !solidIn(cells, x, y - up, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose)
          ) {
            x = nx;
            y -= up;
            stepped = true;
            result.steppedUp = true;
            break;
          }
        }
      }
      if (!stepped) {
        result.hitX = true;
        vx = 0;
        break;
      }
    }
  } else if (phased) {
    x += dx;
  }

  // --- Y axis ---
  const dy = vy * dt;
  if (dy !== 0 && !phased) {
    const steps = Math.max(1, Math.ceil(Math.abs(dy) / MAX_SUBSTEP));
    const inc = dy / steps;
    for (let s = 0; s < steps; s++) {
      const ny = y + inc;
      // Only a descending body can land on a one-way floor, and the reference
      // height is the feet *before* this increment — see `solidIn`. Rising and
      // drop-through both pass the sentinel, so semi-solids vanish for them.
      const feet = inc > 0 && !dropThrough ? y + halfH : ONE_WAY_IGNORE;
      if (!squeezeBlocked(cells, x, ny, halfW, halfH, phased, passLoose, limits.minGapCells, feet)) {
        y = ny;
        continue;
      }

      // Rising into a ceiling: try a horizontal nudge before killing the jump.
      // A jump that clips a corner completing is worth a lot of goodwill.
      if (inc < 0 && (flags & BodyFlag.NO_STEP_UP) === 0) {
        let corrected = false;
        for (let d = 1; d <= limits.cornerCorrection; d++) {
          for (const sgn of [1, -1]) {
            if (!solidIn(cells, x + sgn * d, ny, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose)) {
              x += sgn * d;
              y = ny;
              corrected = true;
              result.cornerCorrected = true;
              break;
            }
          }
          if (corrected) break;
        }
        if (corrected) continue;
      }

      result.hitY = true;
      if (inc > 0) {
        result.landed = true;
        result.landingSpeed = vy;
      }
      const b = w.bounce[i];
      vy = b > 0 ? -vy * b : 0;
      break;
    }
  } else if (phased) {
    y += dy;
  }

  // --- contact state ---
  const wasGrounded = w.grounded[i] === 1;
  // The ground probe must see semi-solids or a body would stand on a catwalk
  // with no friction, no jump and no coyote time. Feet are already resting on
  // the surface at this point, so the current position is the right reference.
  const groundFeet = dropThrough ? ONE_WAY_IGNORE : y + halfH;
  const grounded = !phased && solidIn(cells, x, y + 1, halfW, halfH, phased, groundFeet, passLoose);
  w.grounded[i] = grounded ? 1 : 0;
  if (grounded && !wasGrounded && !result.landed) {
    result.landed = true;
    result.landingSpeed = Math.abs(vy);
  }

  let onWall = 0;
  if (!phased && !grounded) {
    if (solidIn(cells, x + 1.5, y, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose)) onWall = 1;
    else if (solidIn(cells, x - 1.5, y, halfW, halfH, phased, ONE_WAY_IGNORE, passLoose)) onWall = -1;
  }
  w.onWall[i] = onWall;

  w.groundMat[i] = grounded ? groundMaterialAt(cells, x, y, halfW, halfH) : VOID;
  w.groundSlope[i] = grounded ? groundSlopeAt(cells, x, y, halfW, halfH) : 0;

  w.x[i] = x;
  w.y[i] = y;
  w.vx[i] = vx;
  w.vy[i] = vy;

  return result;
}

/**
 * Push loose cells out of the way as the body moves. Bodies flagged
 * DISPLACES_CELLS wade through sand rather than standing on it, which is what
 * sells "the world is matter" during ordinary walking.
 */
export function displaceCells(w: World, i: number, cells: CellWorld, particles?: {
  promote(x: number, y: number, vx: number, vy: number): boolean;
}): void {
  const x = w.x[i];
  const y = w.y[i];
  const halfW = w.halfW[i];
  const halfH = w.halfH[i];
  const vx = w.vx[i];
  const vy = w.vy[i];
  const speed = Math.hypot(vx, vy);
  if (speed < 20) return;

  const x0 = Math.floor(x - halfW);
  const x1 = Math.floor(x + halfW - 0.001);
  const y0 = Math.floor(y - halfH);
  // One row past the feet when grounded. A body standing on sand never
  // *overlaps* it — collision and step-up resolve it out of the surface every
  // substep — so sweeping the footprint alone meant this only ever fired for a
  // body already buried, and "walking across sand displaces sand"
  // (docs/02 §5) could not happen. The surface you are standing on is the
  // surface you scuff.
  const y1 = Math.floor(y + halfH - 0.001) + (w.grounded[i] === 1 ? 1 : 0);

  for (let cy = y0; cy <= y1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      const m = cells.peek(cx, cy);
      if (m === VOID) continue;
      const cat = M.category[m];
      if (cat !== Category.LOOSE && cat !== Category.LIQUID) continue;
      if (cells.getFlags(cx, cy) & Flag.STATIC) continue;
      // Throw it in the direction of travel, scaled down so walking scuffs
      // and sprinting sprays.
      if (particles) {
        particles.promote(cx, cy, vx * 0.35, vy * 0.35 - 30);
      }
    }
  }
}
